"use client";

import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

export interface RadioOption<T extends string> {
  value: T;
  label: string;
  /** Optional secondary hint shown muted after the label. */
  hint?: string;
}

/**
 * Radio-group control for small option sets (preferred over a dropdown when
 * there are fewer than ~5 choices). Generic over the option value type.
 */
export function OptionRadioGroup<T extends string>({
  name,
  label,
  value,
  onChange,
  options,
  disabled = false,
}: {
  name: string;
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: RadioOption<T>[];
  disabled?: boolean;
}) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{label}</p>
      <RadioGroup
        value={value}
        onValueChange={(next) => onChange(next as T)}
        disabled={disabled}
        className="gap-2"
      >
        {options.map((option) => {
          const id = `${name}-${option.value}`;
          return (
            <Label
              key={option.value}
              htmlFor={id}
              className="flex cursor-pointer items-center gap-2 font-normal"
            >
              <RadioGroupItem id={id} value={option.value} />
              <span>
                {option.label}
                {option.hint ? <span className="text-foreground/60"> — {option.hint}</span> : null}
              </span>
            </Label>
          );
        })}
      </RadioGroup>
    </div>
  );
}
